<script setup>
/* eslint-disable camelcase */
import CustomConfirmationDialog from '@/components/common/CustomConfirmationDialog.vue'
import CustomDatatable from '@/components/common/CustomDatatable.vue'
import CustomForm from '@/components/common/CustomForm.vue'
import axios from '@/utils/axios'
import { toast } from 'vue3-toastify'

const repCodes = ref({ data: [], total: 0 })
const route = useRoute()
const companies = ref([])
const branchCodes = ref([])
const users = ref([])
const filters = ref({ page: 1, perPage: 10 })
const formRef = ref()
const formDialogVisible = ref(false)
const viewDialogVisible = ref(false)
const deleteDialogVisible = ref(false)
const saving = ref(false)
const selectedRepCode = ref(null)
const formData = ref(emptyForm())

const permissions = Object.values(JSON.parse(localStorage.getItem('userData') || '{}').permissions_by_roles || {}).flat()
const canCreate = permissions.includes('Create Rep Code')
const canEdit = permissions.includes('Edit Rep Code')
const canDelete = permissions.includes('Delete Rep Code')

const headers = [
  { title: 'Rep Code', key: 'rep_code', sortable: true },
  { title: 'Investment Company', key: 'investment_company', sortable: true },
  { title: 'Branch Code', key: 'branch_code', sortable: false },
  { title: 'Assigned Users', key: 'users_count', sortable: true },
  { title: 'Description / Notes', key: 'description', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false },
]

function emptyForm() {
  return { id: null, rep_code: '', investment_company_id: null, investment_company_branch_code_id: null, description: '', user_ids: [] }
}

const companyOptions = computed(() => companies.value.map(company => ({ title: company.title, value: company.id })))
const branchCodeOptions = computed(() => branchCodes.value.map(branchCode => ({ title: branchCode.branch_code, value: branchCode.id })))

const userOptions = computed(() => users.value.map(user => ({
  title: `${user.first_name} ${user.last_name}`.trim() + (user.email ? ` (${user.email})` : ''),
  value: user.id,
})))

const repCodeRules = [
  value => !!value || 'Rep code is required',
  value => /^[A-Za-z0-9]+$/.test(value || '') || 'Use letters and numbers only',
  value => String(value || '').length <= 12 || 'Maximum 12 characters',
]

async function fetchReferenceData() {
  const [companyResponse, userResponse] = await Promise.all([
    axios.get('/investment_companies?all=1'),
    axios.get('/users?per_page=250'),
  ])

  if (companyResponse.status === 'success')
    companies.value = companyResponse.data.investment_companies || []
  if (userResponse.status === 'success')
    users.value = userResponse.data.users?.data || userResponse.data.users || []
}

async function fetchRepCodes(payload = filters.value) {
  filters.value = { ...filters.value, ...payload }

  const response = await axios.get('/rep_codes', { params: {
    page: filters.value.page || 1,
    per_page: filters.value.perPage || 10,
    search_query: filters.value.searchQuery || undefined,
    sort_by: filters.value.sortBy || undefined,
    order_by: filters.value.orderBy || undefined,
    investment_company_id: filters.value.investmentCompanyId || undefined,
  } })

  if (response.status === 'success')
    repCodes.value = response.data.rep_codes
  else
    toast.error(response.message)
}

async function fetchBranchCodes(companyId) {
  branchCodes.value = []
  if (!companyId)
    return
  const response = await axios.get('/branch_codes', { params: { investment_company_id: companyId, per_page: 250 } })
  if (response.status === 'success')
    branchCodes.value = response.data.branch_codes?.data || []
}

function companyChanged(companyId) {
  formData.value.investment_company_branch_code_id = null
  fetchBranchCodes(companyId)
}

async function addItem() {
  formData.value = emptyForm()
  formData.value.investment_company_id = route.query.investment_company_id ? Number(route.query.investment_company_id) : null
  await fetchBranchCodes(formData.value.investment_company_id)
  formDialogVisible.value = true
}

async function editItem(item) {
  formData.value = {
    id: item.id,
    rep_code: item.rep_code,
    investment_company_id: item.investment_company_id,
    investment_company_branch_code_id: item.investment_company_branch_code_id,
    description: item.description || '',
    user_ids: [...(item.user_ids || [])],
  }
  await fetchBranchCodes(item.investment_company_id)
  formDialogVisible.value = true
}

async function submitForm() {
  const validation = await formRef.value?.validate()
  if (!validation?.valid)
    return
  saving.value = true
  try {
    const payload = { ...formData.value, rep_code: formData.value.rep_code.trim().toUpperCase() }
    const response = payload.id ? await axios.put(`/rep_codes/${payload.id}`, payload) : await axios.post('/rep_codes', payload)
    if (response.status === 'success') {
      toast.success(response.message)
      formDialogVisible.value = false
      await fetchRepCodes()
    } else {
      toast.error(response.message)
    }
  } finally {
    saving.value = false
  }
}

async function confirmDelete(confirmed) {
  if (!confirmed) {
    deleteDialogVisible.value = false
    
    return
  }
  const response = await axios.delete(`/rep_codes/${selectedRepCode.value.id}`)
  if (response.status === 'success') {
    toast.success(response.message)
    deleteDialogVisible.value = false
    await fetchRepCodes()
  } else {
    toast.error(response.message)
  }
}

onMounted(async () => {
  if (route.query.investment_company_id)
    filters.value.investmentCompanyId = Number(route.query.investment_company_id)
  await Promise.all([fetchReferenceData(), fetchRepCodes()])
})

definePage({ meta: { requiresAuth: true } })
</script>

<template>
  <div>
    <div class="mb-4">
      <h3 class="text-h3 text-high-emphasis mb-1">
        Rep Codes
      </h3>
      <div>Manage investment-company rep codes and their user assignments.</div>
    </div>
    <VCard>
      <VCardText class="pb-0">
        <AppSelect
          v-model="filters.investmentCompanyId"
          :items="companyOptions"
          label="Investment Company"
          placeholder="All investment companies"
          clearable
          style="max-inline-size: 360px"
          @update:model-value="fetchRepCodes({ ...filters, page: 1, investmentCompanyId: $event })"
        />
      </VCardText>
      <VCardText class="pa-0">
        <CustomDatatable
          :headers="headers"
          :table-data="repCodes"
          @table-changed="fetchRepCodes"
          @search-value="searchQuery => fetchRepCodes({ ...filters, page: 1, searchQuery })"
        >
          <template #add_button>
            <VBtn
              v-if="canCreate"
              prepend-icon="tabler-plus"
              @click="addItem"
            >
              Add Rep Code
            </VBtn>
          </template>
          <template #item.rep_code="{ item }">
            <span class="font-weight-medium text-primary">{{ item.rep_code }}</span>
          </template>
          <template #item.investment_company="{ item }">
            {{ item.investment_company?.title || '—' }}
          </template>
          <template #item.branch_code="{ item }">
            {{ item.branch_code?.branch_code || '—' }}
          </template>
          <template #item.users_count="{ item }">
            <VChip
              v-for="user in item.users"
              :key="user.id"
              size="small"
              color="primary"
              variant="tonal"
              class="me-1 mb-1"
            >
              {{ user.first_name }}
            </VChip>
            <span
              v-if="!item.users?.length"
              class="text-medium-emphasis"
            >—</span>
          </template>
          <template #item.description="{ item }">
            <span class="text-wrap">{{ item.description || '—' }}</span>
          </template>
          <template #table_actions="{ item }">
            <div class="d-flex gap-1">
              <VBtn
                size="x-small"
                color="primary"
                @click="selectedRepCode = item; viewDialogVisible = true"
              >
                <VIcon icon="tabler-eye" />
              </VBtn>
              <VBtn
                v-if="canEdit"
                size="x-small"
                color="primary"
                @click="editItem(item)"
              >
                <VIcon icon="tabler-pencil" />
              </VBtn>
              <VBtn
                v-if="canDelete"
                size="x-small"
                color="error"
                @click="selectedRepCode = item; deleteDialogVisible = true"
              >
                <VIcon icon="tabler-trash" />
              </VBtn>
            </div>
          </template>
        </CustomDatatable>
      </VCardText>
    </VCard>

    <CustomForm
      v-model:is-dialog-visible="formDialogVisible"
      :is-dialog-visible="formDialogVisible"
      :title="formData.id ? 'Edit Rep Code' : 'Add Rep Code'"
      description="Set the investment company and assign one or more users."
      :width="700"
    >
      <template #form>
        <VForm
          ref="formRef"
          class="mt-6"
          @submit.prevent="submitForm"
        >
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <AppTextField
                v-model="formData.rep_code"
                label="Rep Code *"
                placeholder="Up to 12 letters or numbers"
                maxlength="12"
                :counter="12"
                :rules="repCodeRules"
                @update:model-value="formData.rep_code = String($event || '').toUpperCase()"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <AppSelect
                v-model="formData.investment_company_id"
                :items="companyOptions"
                label="Investment Company *"
                placeholder="Select a company"
                :rules="[value => !!value || 'Investment company is required']"
                @update:model-value="companyChanged"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <AppSelect
                v-model="formData.investment_company_branch_code_id"
                :items="branchCodeOptions"
                label="Branch Code"
                placeholder="Optional branch code"
                clearable
                :disabled="!formData.investment_company_id"
              />
            </VCol>
            <VCol cols="12">
              <AppCombobox
                v-model="formData.user_ids"
                :items="userOptions"
                label="Assigned Users"
                placeholder="Select one or more users"
                multiple
                chips
                closable-chips
                :return-object="false"
              />
            </VCol>
            <VCol cols="12">
              <AppTextarea
                v-model="formData.description"
                label="Description / Notes"
                placeholder="Optional notes"
                rows="4"
              />
            </VCol>
            <VCol
              cols="12"
              class="d-flex justify-end gap-3"
            >
              <VBtn
                color="secondary"
                variant="tonal"
                @click="formDialogVisible = false"
              >
                Cancel
              </VBtn>
              <VBtn
                type="submit"
                :loading="saving"
              >
                {{ formData.id ? 'Update' : 'Create' }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </template>
    </CustomForm>

    <VDialog
      v-model="viewDialogVisible"
      max-width="650"
    >
      <VCard title="Rep Code Details">
        <VCardText v-if="selectedRepCode">
          <VList>
            <VListItem
              title="Rep Code"
              :subtitle="selectedRepCode.rep_code"
            />
            <VListItem
              title="Investment Company"
              :subtitle="selectedRepCode.investment_company?.title || '—'"
            />
            <VListItem
              title="Branch Code"
              :subtitle="selectedRepCode.branch_code?.branch_code || '—'"
            />
            <VListItem
              title="Description / Notes"
              :subtitle="selectedRepCode.description || '—'"
            />
          </VList>
          <div class="text-subtitle-1 font-weight-medium mt-3 mb-2">
            Assigned Users
          </div>
          <VChip
            v-for="user in selectedRepCode.users"
            :key="user.id"
            class="me-2 mb-2"
            color="primary"
            variant="tonal"
          >
            {{ user.name }}
          </VChip>
          <div
            v-if="!selectedRepCode.users?.length"
            class="text-medium-emphasis"
          >
            No users assigned.
          </div>
        </VCardText>
        <VCardActions class="justify-end">
          <VBtn
            variant="tonal"
            @click="viewDialogVisible = false"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <CustomConfirmationDialog
      :is-dialog-visible="deleteDialogVisible"
      title="Delete this rep code?"
      description="Its user assignments will also be removed. Historical records remain safely soft-deleted."
      icon="tabler-exclamation-circle"
      icon-color="error"
      confirm-button-text="Delete"
      confirm-button-color="error"
      cancel-button-text="Cancel"
      @confirmation="confirmDelete"
    />
  </div>
</template>
